import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDesktopAgentStatus } from "../hooks/useDesktopAgentStatus";
import { api } from "../utils/api";
import type { DesktopWindowsRelease } from "../types";

export default function DesktopAgent() {
  const { status, loading, error } = useDesktopAgentStatus();
  const [release, setRelease] = useState<DesktopWindowsRelease | null>(null);
  const [releaseLoading, setReleaseLoading] = useState(true);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setReleaseLoading(true);
    setReleaseError(null);

    api<DesktopWindowsRelease>("/api/desktop/release/windows")
      .then((data) => {
        if (cancelled) return;
        setRelease(data);
        setReleaseLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setReleaseError(err?.message || "Failed to load Windows installer metadata.");
        setReleaseLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const latestDevice = status?.latestDevice || null;
  const statusTone =
    status?.status === "connected"
      ? "pill pill--success"
      : status?.status === "needs_attention"
        ? "pill pill--accent"
        : "pill";

  const statusTitle =
    status?.status === "connected"
      ? "Desktop agent connected"
      : status?.status === "needs_attention"
        ? "Desktop agent needs attention"
        : status?.status === "offline"
          ? "Desktop agent offline"
          : "Desktop agent not installed";

  const statusCopy =
    status?.status === "connected"
      ? "Your Windows agent is connected and ready to watch telemetry automatically."
      : status?.status === "needs_attention"
        ? "The app is installed, but a required service is not fully running. Open the desktop agent and turn it back on."
        : status?.status === "offline"
          ? "A Windows agent has been installed before, but it is not sending a recent heartbeat."
          : "Install the Windows desktop agent to automate telemetry uploads instead of relying on manual fallback uploads.";

  return (
    <div className="page page--wide fade-in">
      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">Windows desktop agent</span>
          </div>
          <div className="editorial-meta-right">
            <Link to="/settings" className="btn btn-secondary">
              Back to settings
            </Link>
            {release?.available && release.downloadUrl ? (
              <a
                href={release.downloadUrl}
                className="btn btn-primary"
                download={release.filename || true}
              >
                Download Windows agent
              </a>
            ) : (
              <button className="btn btn-primary" disabled>
                Windows installer pending
              </button>
            )}
          </div>
        </div>
        <h1 className="editorial-title">Install once. Let uploads run themselves.</h1>
        <p className="editorial-dek">
          The desktop agent watches the iRacing telemetry folder, captures IRSDK sidecars, and sends new runs to StintSense automatically.
        </p>
      </header>

      <div style={styles.topGrid}>
        <section className="section-shell" style={styles.section}>
          <div className="section-header">
            <div>
              <div className="section-kicker">Current status</div>
              <div className="section-heading">{statusTitle}</div>
            </div>
            <span className={statusTone}>{status?.status?.replace("_", " ") || "Loading"}</span>
          </div>

          <div style={styles.sectionBody}>
            {loading ? (
              <div className="empty-state" style={{ minHeight: "12rem" }}>
                <div className="spinner spinner-lg" />
                <div className="empty-state-title">Checking your desktop agent</div>
              </div>
            ) : error ? (
              <div className="empty-state" style={{ minHeight: "12rem" }}>
                <div className="empty-state-title">Status unavailable</div>
                <div className="empty-state-text">{error}</div>
              </div>
            ) : (
              <>
                <p className="page-copy" style={{ marginBottom: "1rem" }}>{statusCopy}</p>
                <div style={styles.statGrid}>
                  <MetricTile label="Machine" value={latestDevice?.machineName || "Not installed"} />
                  <MetricTile label="Team" value={latestDevice?.teamName || "Not selected"} />
                  <MetricTile label="Version" value={latestDevice?.appVersion || "Unknown"} />
                  <MetricTile label="Last Seen" value={formatRelative(latestDevice?.lastSeenAt)} />
                </div>
                {latestDevice?.lastError && (
                  <div style={styles.alertCard}>
                    <div style={styles.alertTitle}>Last agent error</div>
                    <div style={styles.alertBody}>{latestDevice.lastError}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <section className="section-shell" style={styles.section}>
          <div className="section-header">
            <div>
              <div className="section-kicker">Download</div>
              <div className="section-heading">Windows installer</div>
            </div>
          </div>

          <div style={styles.sectionBody}>
            {releaseLoading ? (
              <div className="empty-state" style={{ minHeight: "12rem" }}>
                <div className="spinner spinner-lg" />
                <div className="empty-state-title">Checking release metadata</div>
              </div>
            ) : releaseError ? (
              <div className="empty-state" style={{ minHeight: "12rem" }}>
                <div className="empty-state-title">Installer metadata unavailable</div>
                <div className="empty-state-text">{releaseError}</div>
              </div>
            ) : (
              <>
                <div style={styles.releaseHero}>
                  <div>
                    <div className="page-kicker" style={{ marginBottom: "0.45rem" }}>Windows only</div>
                    <div style={styles.releaseTitle}>{release?.filename || "StintSense Agent"}</div>
                    <div style={styles.releaseMeta}>
                      {release?.version ? `Version ${release.version}` : "Version pending"}
                      {release?.publishedAt ? ` | ${new Date(release.publishedAt).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  {release?.available && release.downloadUrl ? (
                    <a
                      href={release.downloadUrl}
                      className="btn btn-primary"
                      download={release.filename || true}
                    >
                      Download installer
                    </a>
                  ) : (
                    <button className="btn btn-secondary" disabled>
                      Not published yet
                    </button>
                  )}
                </div>

                <p className="page-copy" style={{ marginTop: "1rem" }}>
                  {release?.message || "Install the agent on the Windows PC that runs iRacing so uploads happen automatically after each session."}
                </p>

                {release?.releaseNotesUrl && (
                  <div style={{ marginTop: "0.85rem" }}>
                    <a href={release.releaseNotesUrl} target="_blank" rel="noreferrer" className="btn btn-secondary">
                      View release notes
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <section className="section-shell">
        <div className="section-header">
          <div>
            <div className="section-kicker">Setup checklist</div>
            <div className="section-heading">How drivers should install it</div>
          </div>
        </div>
        <div style={styles.checklist}>
          <ChecklistStep
            step="1"
            title="Download and install the Windows agent"
            body="Use the installer above on the PC that runs iRacing. No separate browser extension or helper is required."
          />
          <ChecklistStep
            step="2"
            title="Sign in with the same StintSense account"
            body="The agent uses your existing StintSense auth flow, so it already knows who you are and which teams you belong to."
          />
          <ChecklistStep
            step="3"
            title="Select the team and confirm the telemetry folder"
            body="Point the agent at the iRacing telemetry directory once, choose the correct team, and keep the default production API unless you are testing locally."
          />
          <ChecklistStep
            step="4"
            title="Turn the agent on"
            body="When the agent is on, watcher + telemetry capture should run automatically. Drivers should not need to micromanage subsystems."
          />
          <ChecklistStep
            step="5"
            title="Use manual upload only as fallback"
            body="If the desktop agent misses a run, drivers can still upload `.ibt` files manually from Settings, but the goal is to make that rare."
          />
        </div>
      </section>
    </div>
  );
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.metricTile}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{value}</div>
    </div>
  );
}

function ChecklistStep({
  step,
  title,
  body,
}: {
  step: string;
  title: string;
  body: string;
}) {
  return (
    <article className="card" style={styles.stepCard}>
      <div style={styles.stepIndex}>{step}</div>
      <div>
        <div style={styles.stepTitle}>{title}</div>
        <div style={styles.stepBody}>{body}</div>
      </div>
    </article>
  );
}

function formatRelative(value: string | null | undefined): string {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString();
}

const styles: Record<string, React.CSSProperties> = {
  topGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "1rem",
  },
  section: {
    minHeight: "100%",
  },
  sectionBody: {
    padding: "1.25rem",
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "0.75rem",
  },
  metricTile: {
    padding: "0.95rem",
    borderRadius: "var(--r-md)",
    background: "var(--ink-1)",
    border: "1px solid var(--rule)",
  },
  metricLabel: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  metricValue: {
    marginTop: "0.35rem",
    fontFamily: "var(--font-display)",
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "var(--text-strong)",
  },
  alertCard: {
    marginTop: "1rem",
    padding: "0.95rem",
    borderRadius: "var(--r-md)",
    border: "1px solid rgba(248, 113, 113, 0.24)",
    background: "var(--flag-red-soft)",
  },
  alertTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    color: "var(--flag-red)",
  },
  alertBody: {
    marginTop: "0.4rem",
    color: "var(--text-default)",
    lineHeight: 1.6,
  },
  releaseHero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "1rem",
    flexWrap: "wrap",
  },
  releaseTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "var(--text-strong)",
  },
  releaseMeta: {
    marginTop: "0.35rem",
    color: "var(--text-secondary)",
  },
  checklist: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "0.85rem",
    padding: "1.25rem",
  },
  stepCard: {
    display: "flex",
    gap: "0.9rem",
    alignItems: "flex-start",
    padding: "1rem",
  },
  stepIndex: {
    width: "2rem",
    height: "2rem",
    borderRadius: "999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(91, 141, 239, 0.12)",
    border: "1px solid rgba(91, 141, 239, 0.2)",
    color: "var(--live-strong)",
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    flexShrink: 0,
  },
  stepTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "1rem",
    fontWeight: 700,
    color: "var(--text-strong)",
  },
  stepBody: {
    marginTop: "0.35rem",
    color: "var(--text-secondary)",
    lineHeight: 1.65,
  },
};
